/**
 * Cancel-authorization canonical payload — the SINGLE source of truth (Batch B.1).
 *
 * The /dex/cancel endpoint requires a wallet signature proving the caller owns the order. Both the API
 * verifier (apps/api/src/services/dex/cancel-auth.ts + eip712-verify.ts) AND the web signer
 * (apps/web/src/services/cancelAuth.ts) import the canonical signed content from HERE so the two can
 * never drift: if they signed/verified different bytes, every cancel would silently fail to verify.
 *
 * This module is CONTENT ONLY — no crypto, no chain libs, no I/O. It defines exactly:
 *   - the EVM EIP-712 domain ({name,version}; chainId is added per-chain by the caller) + Cancel types,
 *   - the Tron/Solana canonical human-readable message string + its field order.
 * Signing (wallet adapters / ethers / tronweb / noble) and verification stay in each app.
 */

/**
 * EVM EIP-712 domain for cancel authorizations. DISTINCT from the "ParseReferral" domain so a cancel
 * signature can never be replayed as a referral op (or vice-versa). The caller adds `chainId` →
 * a signature is bound to exactly one chain. NOT `as const`: ethers.verifyTypedData / signTypedData
 * expect a mutable domain/types shape, and a readonly literal triggers TS assignability errors there.
 */
export const CANCEL_DOMAIN: { name: string; version: string } = { name: 'ParseCancel', version: '1' };

/** EIP-712 Cancel struct. Field set + order is part of the signed digest — changing it breaks every sig. */
export const CANCEL_TYPES: { Cancel: Array<{ name: string; type: string }> } = {
  Cancel: [
    { name: 'transferId', type: 'string' },
    { name: 'wallet', type: 'address' },
    { name: 'nonce', type: 'string' },
    { name: 'deadline', type: 'uint256' },
  ],
};

/** The fields that go into a cancel authorization (chain selects the signing/verifying family). */
export interface CancelMessageFields {
  transferId: string;
  wallet: string;
  nonce: string;
  deadline: number; // unix SECONDS
  chain: string;    // the order's source_chain (e.g. 'base', 'tron', 'solana')
}

/**
 * Canonical human-readable cancel message for Tron/Solana (EVM uses EIP-712 typed data, not this string).
 * The user sees these exact lines in their wallet (anti-phishing). The verifier and the signer MUST
 * produce byte-identical output — that is the whole point of co-locating this here. Any change to the
 * literal lines, the field order, the labels, or the '\n' joiner is a breaking change to live signatures.
 */
export function buildCancelMessage(i: CancelMessageFields): string {
  return [
    'ParseCancel v1',
    `transferId: ${i.transferId}`,
    `wallet: ${i.wallet}`,
    `nonce: ${i.nonce}`,
    `deadline: ${i.deadline}`,
    `chain: ${i.chain}`,
  ].join('\n');
}

/**
 * The signing/verifying family a chain label belongs to.
 *
 * `'unknown'` is a FIRST-CLASS member, not an error channel: `chainFamily` is TOTAL. Every consumer
 * must have an arm for it, and that arm must DENY. See the function docblock for why a total
 * function beats a throw here.
 */
export type ChainFamily = 'evm' | 'tron' | 'solana' | 'unknown';

/**
 * The EVM chain labels this classifier RECOGNISES. Enumerated, not assumed.
 *
 * The union of every EVM label that exists anywhere in the codebase today: the backend's seven
 * `EVM_CHAINS` (`apps/api/src/config/index.ts:55`) plus `avalanche`, which is present in the
 * frontend's `CHAIN_REGISTRY` (`apps/web/src/config/chains.ts`) as an `addrType: 'evm'` entry and
 * is therefore reachable here even though the backend has no row for it. Listing all eight is what
 * makes the change below BEHAVIOUR-PRESERVING rather than merely safer.
 *
 * `packages/shared` cannot import either app's config — it is the leaf both depend on — so this is
 * a copy, and it is a deliberately small and enumerable one. It is NOT the address-semantics
 * registry and must not grow into one: re-homing that registry here (and the `Chain`-vocabulary
 * widening it forces) is an excluded design question, recorded on #462.
 */
const EVM_CHAIN_LABELS: ReadonlySet<string> = new Set([
  'ethereum', 'base', 'optimism', 'arbitrum', 'polygon', 'bnb', 'robinhood', 'avalanche',
]);

/**
 * Map a source_chain string to its signing/verifying family. TOTAL — never throws.
 *
 * NO `return 'evm'` DEFAULT ARM (#459 invariant I2). It used to end with one, and that is a
 * lowercasing default in disguise: 'evm' selects EIP-712 signature recovery in the backend verifier
 * (`apps/api/src/services/dex/cancel-auth.ts:96`) and a case-INSENSITIVE ownership compare in both
 * the FE eligibility check (`apps/web/src/utils/cancelEligibility.ts:47-49`) and the backend's
 * user-cancel authority check (`services/dex/auto-cancel.ts:163`). A cancel returns funds to the
 * source wallet, so that ownership compare IS the fund-return authority — and folding a
 * base58check address on both sides of it accepts an address that is not the user's. A bitcoin,
 * litecoin or zcash label (#374) would have landed on exactly that arm.
 *
 * WHY `'unknown'` AND NOT A THROW (operator decision, 2026-08-27, after caller verification).
 * `'unknown'` is NOT the same shape as the `'evm'` default it replaces: `'evm'` was ACTIVELY WRONG
 * — it authorised the wrong comparison semantics on a fund-return authority check — whereas
 * `'unknown'` is INERT and every consumer denies on it. A throw would be the wrong remedy for a
 * classifier consumed by a React render (`cancelEligibility.ts:42`), by a money-path bot loop
 * (`auto-cancel.ts:163`), and by a function whose documented contract is that it NEVER THROWS
 * (`cancel-auth.ts:76`, whose own `chainFamily` call at `:93` sits OUTSIDE its try/catch). A total
 * function with an explicit inert member lets each caller deny locally instead of forcing every
 * caller to wrap it — and keeps that "never throws" contract true.
 *
 * WHY IT LOGS. `'unknown'` fails closed but SILENTLY: a user whose chain is unrecognised simply
 * cannot cancel, with nothing in the logs to say why. The `warn` line naming the label is the ONLY
 * signal that a new chain has reached this classifier without an arm — which is precisely what
 * #374 (bitcoin/litecoin/zcash) will produce. `console.warn` rather than a logger by necessity:
 * this package is isomorphic (bundled into the browser by Vite AND compiled under the backend's
 * CommonJS setting — see `apps/web/vite.config.ts:11`), has zero dependencies by design, and
 * cannot import either app's logger without inverting that leaf position.
 *
 * BEHAVIOUR-PRESERVING TODAY, and provably so rather than assertedly: no chain label outside
 * `{solana*, tron} ∪ EVM_CHAIN_LABELS` exists anywhere in this codebase, so the old default arm was
 * unreachable. `apps/api/tests/address-semantics/chain-family-domain.test.ts` enumerates the full
 * domain — the backend registry's nine labels, the frontend registry's ten, the `solana-devnet`
 * form pinned by `apps/api/tests/dex/cancel-message-consistency.test.ts:80`, and the six
 * `packages/shared` CCTP labels — and asserts byte-identical results across the change.
 *
 * EVERY CONSUMER MUST DENY ON `'unknown'`, and each does so STRUCTURALLY (not by accident of what
 * some downstream library rejects):
 *   - `auto-cancel.ts:163` / `cancelEligibility.ts:48` — `family === 'evm' ? fold : exact`; an
 *     unknown label takes the EXACT compare, the strict one.
 *   - `cancel-auth.ts:96-121` — explicit `evm` / `tron` / `solana` arms + a terminal `else` that
 *     returns `{ ok: false, reason: 'unknown_chain' }` directly (commit `d8dd9187` replaced the
 *     bare `sigOk = false` fallthrough this docblock used to describe — checked at HEAD,
 *     `cancel-auth.ts:109-120`).
 *   - `useCancelOrder.ts:78-107` — the same three arms + a terminal `else` that throws a named,
 *     honest user-facing error instead of prompting for a Solana wallet (checked at HEAD, still
 *     accurate — no drift here).
 */
export function chainFamily(chain: string): ChainFamily {
  if (chain.includes('solana')) return 'solana';
  if (chain === 'tron') return 'tron';
  if (EVM_CHAIN_LABELS.has(chain)) return 'evm';
  console.warn(
    `chainFamily: unknown chain '${chain}' -> 'unknown'. This function selects a SIGNING/VERIFYING ` +
    `family and a cancel-ownership comparison, so there is no safe default: assuming EVM would ` +
    `case-fold a base58check or bech32 address on both sides of a fund-return authority check. ` +
    `Every caller DENIES on 'unknown', so this label cannot cancel until it is given an arm: add ` +
    `it to EVM_CHAIN_LABELS if it is genuinely EVM, or give the family an explicit arm.`,
  );
  return 'unknown';
}
